import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext
} from '@sierralabs/workflow-ui';

import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import JiraStatusPicker from '../../../jira-connect/pickers/JiraStatusPicker';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .event-field {
    margin-top: 12px;
  }
  .MuiTableHead-root .MuiTableCell-root {
    padding-left: 4px;
    padding-right: 4px;
  }
  .MuiTableRow-root.dense .MuiTableCell-root {
    padding: 2px 4px;
  }
`;

export interface TransitionEntityStatusFromJiraParams {
  src: 'transitionEntityStatusFromJira';
  data: {
    projectId?: string;
    eventName?: string;
    partial?: boolean;
    issues?: {
      statuses?: { id: string; name: string }[];
      issueType?: { id: string; name: string };
    }[];
  };
}

const TransitionEntityStatusFromJiraConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { stateNode, onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: TransitionEntityStatusFromJiraParams['data'] = invoke.data;
  const issues = data.issues || [];
  const events = stateNode.events || [];
  const disabled = !data.projectId;
  const selectedEvent = events.find(
    event => data.eventName && data.eventName === event.name
  );

  const update = (data: TransitionEntityStatusFromJiraParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{
          size: 'small',
          label: 'Project',
          placeholder: 'Select Project'
        }}
        onChange={projectId => {
          update({ ...data, projectId: projectId, issues: [] });
        }}
      />
      <Autocomplete
        options={events}
        value={selectedEvent || null}
        className='event-field'
        getOptionLabel={option => {
          const target = (option.transitions || [])
            .map(transition => transition.target)
            .join(', ');
          return `${option.name} (${stateNode.name} -> ${target})`;
        }}
        onChange={(_, value) => update({ ...data, eventName: value?.name })}
        renderInput={params => (
          <TextField
            {...params}
            size='small'
            variant='outlined'
            label='Event Transition'
            placeholder='Select Event Transition'
          />
        )}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '40%' }}>Issue Type</TableCell>
              <TableCell>Statuses</TableCell>
              <TableCell style={{ width: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  {data.projectId ? (
                    'No items added.'
                  ) : (
                    <>
                      Select a <strong>Project</strong> to add items.
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
            {issues.map((issue, index) => (
              <TableRow key={index} className='dense'>
                <TableCell>
                  <JiraIssueTypePicker
                    value={issue.issueType?.id}
                    projectId={data.projectId}
                    disabled={disabled}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Select Issue Type'
                    }}
                    onChange={issueType => {
                      issues[index].issueType = issueType && {
                        id: issueType.id,
                        name: issueType.label
                      };
                      issues[index].statuses = undefined;
                      update({ ...data, issues });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraStatusPicker
                    multiple
                    projectId={data.projectId || ''}
                    issueTypeId={issue.issueType?.id || ''}
                    disabled={disabled || !issue.issueType?.id}
                    value={(issue.statuses || []).map(status => status.id)}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Select Statuses'
                    }}
                    onChange={statuses => {
                      issues[index].statuses = statuses;
                      update({ ...data, issues });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size='small'
                    disabled={disabled}
                    onClick={() => {
                      issues.splice(index, 1);
                      update({ ...data, issues });
                    }}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          variant='outlined'
          disabled={disabled}
          startIcon={<Add />}
          onClick={() => {
            issues.push({});
            update({ ...data, issues });
          }}
        >
          Add Item
        </Button>
      </Box>
      <Box mt={1}>
        <FormControl fullWidth>
          <RadioGroup
            value={data.partial ? 'partial' : 'all'}
            onChange={event => {
              update({ ...data, partial: event.target.value === 'partial' });
            }}
          >
            <FormControlLabel
              value='all'
              label='All linked issues'
              disabled={disabled}
              control={<Radio color='primary' />}
            />
            <FormControlLabel
              value='partial'
              label='One or more linked issues'
              disabled={disabled}
              control={<Radio color='primary' />}
            />
          </RadioGroup>
          <FormHelperText>
            Transition will trigger when{' '}
            <strong>{data.partial ? 'one or more' : 'all'}</strong> linked
            issues reach the selected statuses.
          </FormHelperText>
        </FormControl>
      </Box>
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  .icon {
    font-size: 18px;
    margin-top: -3px;
    vertical-align: middle;
  }
`;

const TransitionEntityStatusFromJiraDisplay: React.FC<
  ServiceDisplayProps
> = props => {
  const { invoke } = props;
  const data: TransitionEntityStatusFromJiraParams['data'] | undefined =
    invoke.data;
  const issues = data?.issues || [];
  return !data ? (
    <></>
  ) : (
    <StyledDisplayDiv>
      <div>Event: {data.eventName || '?'}</div>
      <div>Check: {data.partial ? 'Partial' : 'All'}</div>
      <div>
        Issues:{' '}
        {issues
          .map(issues => {
            const issueType = issues.issueType?.name;
            const status = (issues.statuses || [])
              .map(status => status.name)
              .join(', ');
            return `${issueType} (${status})`;
          })
          .join(', ') || '?'}
      </div>
    </StyledDisplayDiv>
  );
};

export const TransitionEntityStatusFromJiraPlugin: ServicePlugin<TransitionEntityStatusFromJiraParams> =
  {
    src: 'transitionEntityStatusFromJira',
    name: 'Transition Entity Status From Jira',
    configurator: TransitionEntityStatusFromJiraConfigurator,
    display: TransitionEntityStatusFromJiraDisplay,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data =
        condition.data as TransitionEntityStatusFromJiraParams['data'];
      return !!(
        data &&
        data.projectId &&
        data.eventName &&
        Array.isArray(data.issues) &&
        data.issues.length > 0 &&
        data.issues.every(issue => {
          return (
            issue.issueType &&
            Array.isArray(issue.statuses) &&
            issue.statuses.length > 0
          );
        })
      );
    },
    service: (
      state: State<TaskContext>,
      params:
        | TransitionEntityStatusFromJiraParams
        | TransitionEntityStatusFromJiraParams[]
    ) => {
      console.log('transitionEntityStatusFromJira params', state, params);
      return true;
    }
  };
